import { ChangeDetectionStrategy, Component, effect, inject, input, untracked } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { EshavaEmptyState, TranslatePipe } from 'eshava-shared';

import { ProductContextService } from './product-context.service';

/**
 * Sits above the four areas that are shown **for one product**, and answers the case where none is.
 *
 * The navigation entries stay active, and that is the decision rather than an omission. A disabled
 * menu entry is the same defect as a disabled icon with a tooltip: there is nowhere to say why, and
 * under a finger there is no tooltip at all. The screen is the place that can explain.
 *
 * **Two different reasons for nothing, two different answers** -- which is the argument that settled
 * it, because a disabled entry collapses them into one silent state:
 *
 *   * There **are** products and none is selected. "Choose one" is the whole answer, and the control
 *     is a few centimetres away in the top bar.
 *   * There are **no** products at all. Then "choose a product" is a dead end -- the dropdown itself
 *     says there is nothing to choose. The answer is where products come from: Settings, under a
 *     subscription.
 *
 * For most customers neither appears: one product is preselected, see `ProductContextService`.
 *
 * One component above all four rather than one check inside each. The areas do not have to know the
 * rule, and a fifth area gets it by being routed underneath.
 *
 * **It is also where the selection meets the address bar.** The product travels in a `product` query
 * parameter, so a reload keeps the context, a shared link shows the recipient the same thing, and a
 * jump mark opening a new tab does not land in an empty one. Wired here rather than in each area for
 * the same reason as everything else in this component: a fifth area gets it by being routed
 * underneath.
 */
@Component({
  selector: 'portal-product-required',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EshavaEmptyState, RouterLink, RouterOutlet, TranslatePipe],
  template: `
    @if (context.selected() !== null) {
      <router-outlet />
    } @else if (context.isLoading()) {
      <p class="product-required__note">{{ 'common.loading' | t }}</p>
    } @else if (context.hasProducts()) {
      <eshava-empty-state icon="cubes-solid" titleKey="shell.context.none.title" textKey="shell.context.none.text" />
    } @else {
      <eshava-empty-state
        icon="cubes-solid"
        titleKey="shell.context.empty.title"
        textKey="shell.context.empty.text"
      >
        <a class="product-required__action" routerLink="/settings/subscriptions">
          {{ 'shell.context.empty.action' | t }}
        </a>
      </eshava-empty-state>
    }
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
    }

    .product-required__note {
      color: var(--text-muted);
    }

    .product-required__action {
      display: inline-flex;
      align-items: center;
      min-height: 2.25rem;
      padding: 0 var(--space-3);
      color: var(--on-accent);
      background-color: var(--accent);
      border-radius: var(--radius-sm);
      text-decoration: none;
    }

    .product-required__action:hover {
      color: var(--on-accent);
      background-color: var(--accent-hover);
      text-decoration: none;
    }

    @media (pointer: coarse) {
      .product-required__action {
        min-height: 2.75rem;
      }
    }
  `
})
export class ProductRequired {
  private readonly router = inject(Router);

  protected readonly context = inject(ProductContextService);

  /** From the query parameter, bound by `withComponentInputBinding`. A string, like every route value. */
  readonly product = input<string | undefined>(undefined);

  /**
   * Whether a product has ever been selected in this session.
   *
   * A plain field rather than a signal: nothing renders from it, and making it reactive would put it
   * into the dependencies of the effect that writes it. See the effect for why it exists.
   */
  private everSelected = false;

  /**
   * Whether the address has had its say yet.
   *
   * **This is what makes the two effects independent of the order they run in.** Until the product
   * list has loaded, the address cannot be honoured -- and nothing may write to it either, or a
   * selection restored from `localStorage` would replace the product the link named before the link
   * was ever read.
   */
  private urlHonoured = false;

  /**
   * Two effects, and which of them wins when is the whole design.
   *
   * **On arrival the address decides**, because that is what makes a link worth sharing. Afterwards
   * the selection decides, because that is what the dropdown is for. They cannot fight: each writes
   * only the other side, and each stops as soon as the two agree.
   *
   * The reads that must not become dependencies sit inside `untracked` -- an effect that reacts to
   * what it writes does not error, it hangs the tab. See `CLAUDE.md`.
   *
   * A product the signed-in user cannot see is **not** an error state: the second effect simply puts
   * their own context back into the address. Announcing "you may not see this product" would answer a
   * question the authorisation rules say does not exist.
   */
  constructor() {
    effect(() => {
      const fromUrl = Number(this.product());
      const products = this.context.products();

      /* Nothing to reconcile against yet: an address naming a product cannot be checked, and one
         naming none cannot be told apart from one that has not loaded. */
      if (products.length === 0) {
        return;
      }

      untracked(() => {
        /* Once. Afterwards the selection owns the address -- otherwise switching product in the top
           bar would be undone by the address it is about to replace. */
        if (this.urlHonoured) {
          return;
        }

        this.urlHonoured = true;

        if (Number.isInteger(fromUrl) && products.some(entry => entry.id === fromUrl)) {
          this.context.select(fromUrl);
        }
      });
    });

    effect(() => {
      const selected = this.context.selectedId();
      /*
       * Tracked, not read inside `untracked` -- which is what this originally did, and it made the
       * parameter survive exactly one navigation. Every link inside these areas is written without
       * it, so the address loses it the moment anything is clicked, and nothing brought it back until
       * the product was changed. Reading it here means the effect notices the loss and writes it
       * again, which is what makes the parameter behave like a property of the area rather than of
       * the one navigation that set it.
       */
      const fromUrl = this.product();

      untracked(() => {
        /* Nothing goes into the address before it has been read -- see `urlHonoured`. */
        if (!this.urlHonoured) {
          return;
        }

        /*
         * Cleared deliberately: the parameter goes with it, or the effect above would read it back
         * and the dropdown could never be emptied.
         *
         * **Only once something has been selected**, and that condition is the whole difference
         * between working and not. Without it the first run of this effect -- while the product list
         * is still loading and the selection is therefore null -- strips the parameter out of the
         * address the page was opened with, and the link is dead before the data that would have
         * honoured it arrives. It cost a shared link on 2026-08-26 and looked like the parameter was
         * never read at all.
         */
        if (selected === null) {
          if (fromUrl !== undefined && this.everSelected) {
            void this.router.navigate([], {
              queryParams: { product: null },
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          }

          return;
        }

        this.everSelected = true;

        if (Number(fromUrl) === selected) {
          return;
        }

        /* `replaceUrl`: the context is not a step in the history. Without it, going back would walk
           through every product that has been looked at instead of leaving the area. */
        void this.router.navigate([], {
          queryParams: { product: selected },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      });
    });
  }
}
